import type {
  PlanDetails,
  SubscriptionApiData,
  SubscriptionIntentParams,
} from "@/features/auth/subscription/components/SubscriptionType.ts";

import { env } from "@/config/env.ts";

export function parseSubscriptionSearchParams(): SubscriptionIntentParams {
  const searchParams = new URLSearchParams(window.location.search);

  const stripe_price_id = searchParams.get("stripe_price_id");
  const product_id = searchParams.get("product_id");
  const receipt_email = searchParams.get("receipt_email");
  const metadataRaw = searchParams.get("metadata");

  let metadata: unknown;

  if (metadataRaw) {
    try {
      // Try JSON first
      metadata = JSON.parse(metadataRaw);
    } catch {
      try {
        // Try decodeURIComponent then JSON
        const decoded = decodeURIComponent(metadataRaw);

        metadata = JSON.parse(decoded);
      } catch {
        // Fallback: send raw string
        metadata = metadataRaw;
      }
    }
  }

  return {
    metadata,
    product_id,
    receipt_email,
    stripe_price_id,
  };
}

export async function createSubscriptionIntent(
  params: SubscriptionIntentParams,
  signal?: AbortSignal,
): Promise<SubscriptionApiData> {
  const base = env.VITE_PHP_URL.replace(/\/$/, "");

  //TODO transfer this service to Node
  const res = await fetch(`${base}/api/stripe/intent-from-stripe`, {
    body: JSON.stringify({
      metadata: params.metadata,
      product_id: params.product_id,
      receipt_email: params.receipt_email,
      stripe_price_id: params.stripe_price_id,
    }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
    signal,
  });

  if (!res.ok) {
    throw new Error(`Failed to load payment details (${res.status})`);
  }

  return res.json();
}

export function derivePlanFromApiData(
  apiData: null | SubscriptionApiData | undefined,
): null | PlanDetails {
  if (!apiData) return null;

  const amountCents = apiData.price?.unit_amount ?? apiData.intent?.amount;
  const currencyCode = apiData.price?.currency ?? apiData.intent?.currency;
  const productName = apiData.product?.name;

  if (!productName || typeof amountCents !== "number" || !currencyCode) {
    return null;
  }

  const currency =
    currencyCode?.toUpperCase() === "USD"
      ? "US$"
      : `${currencyCode?.toUpperCase()} `;
  const priceStr = (amountCents / 100).toFixed(2);

  const interval = apiData.price?.recurring?.interval ?? "month";
  const intervalCount = apiData.price?.recurring?.interval_count;
  const billingCycle =
    typeof intervalCount === "number" && intervalCount > 1
      ? `${intervalCount} ${interval}${intervalCount > 1 ? "s" : ""}`
      : (apiData.price?.recurring?.interval ?? "month");

  return {
    billingCycle,
    currency,
    description: apiData.product?.description ?? "",
    features: [],
    name: productName,
    price: priceStr,
  };
}
